import { get, isEmpty } from "lodash";
import React, { useEffect } from "react";
import { trackPromise } from "react-promise-tracker";

import { DocumentUpload } from "../components/DocumentUpload";
import { useCaptureDocumentAttempt } from "../components/report/useCaptureDocumentAttempt";
import {
  generateSignedUrl,
  uploadDocument,
  upsertPolicyElements,
} from "../lib/axle";
import { createEvent } from "../lib/ignitionEvents";
import { retryUploadOperation } from "../lib/uploadRetry";

interface ManualPolicyDocumentProps {
  step: string;
  integration?: string;
  nextStep: (step: string) => void;
  accountInfo: {
    id: string;
  };
  setAccountInfo: (accountInfo: { id: string }) => void;
  policyInfo: {
    id: string;
  };
  setPolicyInfo: (policyInfo: { id: string }) => void;
  loginInformation: any;
  setLoginInformation: (loginInfo: any) => void;
  session: {
    id: string;
    [key: string]: any;
  };
  posthog: any;
  setManualError: (error: string | null) => void;
  setShowNav: (show: boolean) => void;
  client: {
    id: string;
    [key: string]: any;
  };
}

const ManualPolicyDocument = ({
  step,
  integration,
  nextStep,
  accountInfo,
  setAccountInfo,
  policyInfo,
  setPolicyInfo,
  loginInformation,
  setLoginInformation,
  session,
  posthog,
  setManualError,
  setShowNav,
  client,
}: ManualPolicyDocumentProps) => {
  const ignitionToken = session.id;
  const captureDocumentAttempt = useCaptureDocumentAttempt(
    integration,
    loginInformation
  );

  const loadingSteps = ["Uploading your document"];

  const uploadWrapper = async (
    currentFileData: any,
    currentFilePath: string
  ): Promise<void> => {
    const updatedFileData = currentFileData;
    const updatedFilePath = currentFilePath;

    if (!updatedFileData) {
      alert("Please upload a document!");
      return;
    }

    captureDocumentAttempt();
    // Hide nav buttons while CarrierLoader is rendered for accessibility
    setShowNav(false);

    const [signedUrlResponse, signedUrlResponseError] = await generateSignedUrl(
      ignitionToken,
      policyInfo.id,
      updatedFilePath
    );

    if (signedUrlResponseError) {
      switch (get(signedUrlResponseError, "code")) {
        case 500:
          setManualError(get(signedUrlResponseError, "message"));
          nextStep("manual-document-error");
          return;
        default:
          nextStep("connection-error");
          return;
      }
    }

    if (isEmpty(get(signedUrlResponse, "signedUrl"))) {
      console.error("Signed URL is empty");
      setManualError("process");
      nextStep("manual-document-error");
      return;
    }

    captureDocumentAttempt({ documentRef: signedUrlResponse.objectKey });
    const [documentUploadResponse, documentUploadResponseError] =
      await retryUploadOperation(() =>
        uploadDocument(signedUrlResponse.signedUrl, updatedFileData)
      );

    if (documentUploadResponseError) {
      switch (get(documentUploadResponseError, "code")) {
        case 500:
          setManualError(documentUploadResponseError.message);
          nextStep("manual-document-error");
          return;
        default:
          nextStep("connection-error");
          return;
      }
    }

    const documentKey = signedUrlResponse.objectKey;
    const documentsObject = [
      {
        source: "user",
        name: updatedFilePath,
        key: documentKey,
        createdAt: new Date().toISOString(),
        type: null,
        effectiveDate: null,
        issuedDate: null,
      },
    ];

    const policyId = policyInfo.id;
    const accountId = accountInfo.id;

    setLoginInformation({
      ...loginInformation,
      result: "manual",
      resultDetail: "policy-form",
    });

    // Setting account and policy info
    setAccountInfo({ id: accountId });
    setPolicyInfo({ id: policyId });

    // Update policy with document
    const [updatedPolicy, updatedPolicyError] = await upsertPolicyElements(
      ignitionToken,
      policyId,
      {
        account: accountId,
        documents: documentsObject,
      }
    );

    if (updatedPolicyError) {
      switch (get(updatedPolicyError, "code")) {
        case 500:
          setManualError(updatedPolicyError.message);
          nextStep("manual-document-error");
          return;

        default:
          nextStep("connection-error");
          return;
      }
    }

    nextStep("success");
    return;
  };

  const uploadFile = async (
    currentFileData: any,
    currentFilePath: string
  ): Promise<void> => {
    try {
      await trackPromise(
        uploadWrapper(currentFileData, currentFilePath),
        "carrier-loader"
      );
    } finally {
      setShowNav(true);
    }
  };

  /**
   * Main upload function that handles single file uploads for ManualPolicyDocument
   * This component always processes files immediately (no multi-file mode)
   */
  const submitButtonHandler = async (
    currentFileData: any,
    currentFilePath: string
  ) => {
    // Mark this session as having attempted a manual upload, regardless of
    // whether successful or not — used to compute manual-attempt-based conversion metrics
    await createEvent({
      ignitionToken,
      status: "attempted",
      result: "manual",
      resultDetail: "policy-form",
      session,
      client,
    });

    // Process file immediately - no multi-file support for ManualPolicyDocument
    await uploadFile(currentFileData, currentFilePath);
  };

  useEffect(() => {
    posthog.capture("$pageview", { step });
  }, [posthog, step]);

  return (
    <>
      <DocumentUpload
        session={session}
        loginInformation={loginInformation}
        submitButtonHandler={submitButtonHandler}
        loadingSteps={loadingSteps}
        posthog={posthog}
        step={step}
        nextStep={nextStep}
      />
    </>
  );
};

export default ManualPolicyDocument;
